// ============================================
// FILE: /frontend/src/pages/JVCheckoutPage.jsx
// PURPOSE: Joint Venture listing checkout with M-Pesa payment
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { dashboardAPI } from '../api/dashboard.api';
import { jointVenturesAPI } from '../api/jointVentures.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Handshake, ArrowLeft, Phone, CheckCircle, Calendar, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

const JV_PACKAGES = [
  {
    id: '6_months',
    label: '6 Months Listing',
    duration: 6,
    duration_days: 180,
    price: 10000,
    currency: 'KES',
    description: 'Your JV profile listed for 6 months',
  },
  {
    id: '12_months',
    label: '12 Months Listing',
    duration: 12,
    duration_days: 365,
    price: 17000,
    currency: 'KES',
    description: 'Your JV profile listed for 1 year',
    popular: true,
    savings: 'Best Value'
  }
];

export default function JVCheckoutPage() {
  console.log('[JVCheckout] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [selectedPackage, setSelectedPackage] = useState('6_months');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Get form data passed from SubmitJVPage
  const formData = location.state?.formData;

  useEffect(() => {
    // Pre-fill phone number from user profile
    if (user?.phone) {
      setPhoneNumber(user.phone);
    }
  }, [user]);

useEffect(() => {
  const checkActiveSubscription = async () => {
    if (!user?.id) return;
    
    try {
      // Get user's subscription status
      const response = await dashboardAPI.getOverview();
      
      const hasActiveSubscription = 
        response.jvSubscriptionEnd && 
        new Date(response.jvSubscriptionEnd) > new Date();
      
      if (hasActiveSubscription) {
        console.log('[JVCheckout] Active subscription found - skipping payment');
        // Call endpoint to create listing WITHOUT payment
        await jointVenturesAPI.createJVListingDirect(formData);
        
        toast({
          title: 'Success',
          description: 'Your JV listing has been created!'
        });
        
        navigate('/my-submission');
      }
      // If no active subscription, show checkout normally
    } catch (error) {
      console.error('[JVCheckout] Subscription check failed:', error);
      // Continue with normal checkout flow on error
    }
  };
  
  if (formData) {
    checkActiveSubscription();
  }
  }, [formData, user?.id]);

  // ========================================
  // REDIRECT IF NO FORM DATA
  // ========================================
  if (!formData) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-6 sm:py-8 md:py-12 px-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-sm sm:text-base">Invalid Access</AlertTitle>
              <AlertDescription className="text-xs sm:text-sm">
                Please fill out the joint venture form first. <a href="/my-submission" className="underline font-semibold">Go back to form</a>
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const selectedPkgData = JV_PACKAGES.find(p => p.id === selectedPackage);

  // ========================================
  // VALIDATE PHONE NUMBER
  // ========================================
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^254[17]\d{8}$/;
    
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (!phoneRegex.test(phone)) {
      setPhoneError('Invalid format. Use 254XXXXXXXXX (e.g., 254712345678)');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    if (value) {
      validatePhoneNumber(value);
    }
  };

  // ========================================
  // HANDLE PAYMENT
  // ========================================
  const handleProceedToPayment = async () => {
    console.log('[JVCheckout] Proceeding to payment');

    if (!validatePhoneNumber(phoneNumber)) {
      return;
    }

    if (!agreeToTerms) {
      toast({
        variant: 'destructive',
        title: 'Agreement Required',
        description: 'Please agree to the terms and disclaimers to continue.',
      });
      return;
    }

    if (!selectedPkgData || !formData) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Missing package or submission details.',
      });
      return;
    }

    setLoading(true);

    try {
      console.log('[JVCheckout] Initiating JV payment:', {
        package: selectedPkgData.label,
        amount: selectedPkgData.price,
        phoneNumber,
      });

      const response = await jointVenturesAPI.initiateJVPayment({
        packageId: selectedPackage,
        duration: selectedPkgData.duration,
        duration_days: selectedPkgData.duration_days,
        amount: selectedPkgData.price,
        phoneNumber: phoneNumber,
        submissionData: formData,
      });

      console.log('[JVCheckout] Payment initiated:', response);

      if (response.success) {
        // Store payment details for status page
        sessionStorage.setItem('paymentDetails', JSON.stringify({
          paymentId: response.paymentId,
          checkoutRequestId: response.checkoutRequestId,
          tier: `JV_${selectedPkgData.duration}M`,
          amount: selectedPkgData.price,
          phoneNumber: phoneNumber,
          type: 'JV_LISTING',
          packageId: selectedPackage,
          packageLabel: selectedPkgData.label,
        }));

        toast({
          title: 'Payment Initiated',
          description: 'Please check your phone for the M-Pesa prompt.',
        });

        // Navigate to payment status page
        navigate('/subscription/payment-status', {
          state: {
            paymentId: response.paymentId,
            checkoutRequestId: response.checkoutRequestId,
            tier: `JV_${selectedPkgData.duration}M`,
            amount: selectedPkgData.price,
            phoneNumber: phoneNumber,
            type: 'JV_LISTING',
            packageId: selectedPackage,
            submissionData: formData,
          },
        });
      }
    } catch (error) {
      console.error('[JVCheckout] Payment error:', error);
      toast({
        variant: 'destructive',
        title: 'Payment Failed',
        description: error.response?.data?.message || 'Failed to initiate payment. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 lg:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/my-submission')}
            className="mb-4 sm:mb-6 text-xs sm:text-sm"
          >
            <ArrowLeft className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Back
          </Button>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Left: Package Selection */}
            <div className="md:col-span-2">
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center gap-2">
                    <Handshake className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary flex-shrink-0" />
                    <span className="leading-tight">Joint Venture Listing</span>
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm md:text-base">
                    Choose your listing package and enter your M-Pesa details
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                  {/* Submission Summary */}
                  <div className="bg-muted p-3 sm:p-4 rounded-lg">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">Submission Type:</p>
                    <h3 className="text-base sm:text-lg font-bold capitalize">
                      {formData.role === 'LAND_OWNER' ? 'Land Owner' : 'Investor / Partner'}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 sm:mt-2">
                      {formData.role === 'LAND_OWNER' 
                        ? `${formData.landSize} acres • ${formData.developmentType}`
                        : `${formData.experienceLevel} • KES ${formData.investmentAmount?.toLocaleString()}`
                      }
                    </p>
                  </div>

                  {/* Package Selection */}
                  <div className="space-y-2 sm:space-y-3">
                    <Label className="text-sm sm:text-base font-semibold">Select Package</Label>
                    <div className="grid gap-3 sm:gap-4">
                      {JV_PACKAGES.map((pkg) => (
                        <div
                          key={pkg.id}
                          onClick={() => setSelectedPackage(pkg.id)}
                          className={`border-2 rounded-lg p-3 sm:p-4 cursor-pointer transition-all ${
                            selectedPackage === pkg.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm sm:text-base font-semibold">{pkg.label}</h3>
                                {pkg.popular && (
                                  <span className="text-[10px] sm:text-xs bg-primary text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                                    {pkg.savings}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-muted-foreground">{pkg.description}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary">
                                KES {pkg.price.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Phone Number Input */}
                  <div className="space-y-1.5 sm:space-y-2 border-t pt-4 sm:pt-6">
                    <Label htmlFor="phone" className="text-xs sm:text-sm">M-Pesa Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 sm:top-3 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="254712345678"
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        className={`pl-9 sm:pl-10 text-xs sm:text-sm h-9 sm:h-10 ${phoneError ? 'border-red-500' : ''}`}
                        maxLength={12}
                      />
                    </div>
                    {phoneError && (
                      <p className="text-xs sm:text-sm text-red-500">{phoneError}</p>
                    )}
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      Enter the phone number registered with M-Pesa. Format: 254XXXXXXXXX
                    </p>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2">How Payment Works:</h4>
                    <ul className="text-xs sm:text-sm space-y-1 text-muted-foreground">
                      <li>• You will receive an M-Pesa prompt on your phone</li>
                      <li>• Enter your M-Pesa PIN to complete payment</li>
                      <li>• Your submission will be listed immediately after payment</li>
                      <li>• Listing is valid for {selectedPkgData?.duration} months</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Disclaimers & Order Summary */}
            <div className="space-y-3 sm:space-y-4">
              {/* Order Summary */}
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 sm:space-y-3 p-4 sm:p-6 pt-0">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-muted-foreground">Package</span>
                    <span className="font-medium">{selectedPkgData?.label}</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{selectedPkgData?.duration} months</span>
                  </div>
                  <div className="border-t pt-2 sm:pt-3 flex justify-between">
                    <span className="text-sm sm:text-base font-semibold">Total</span>
                    <span className="text-lg sm:text-xl font-bold text-primary">
                      KES {selectedPkgData?.price.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Disclaimers */}
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2 sm:pb-3 p-4 sm:p-6">
                  <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                    <span>Important Disclaimers</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 sm:space-y-3 text-xs sm:text-sm p-4 sm:p-6 pt-0">
                  <div>
                    <p className="font-semibold text-amber-900 mb-0.5 sm:mb-1">No Match Guarantee</p>
                    <p className="text-amber-800">
                      While we work to connect landowners with investors, listing does not guarantee a match. Matching depends on compatibility of your requirements with available partners.
                    </p>
                  </div>

                  <div className="border-t border-amber-200 pt-2 sm:pt-3">
                    <p className="font-semibold text-amber-900 mb-0.5 sm:mb-1">Due Diligence Required</p>
                    <p className="text-amber-800">
                      Before engaging with any potential partner, conduct thorough due diligence. Verify credentials, references, financial capacity, and legal standing. We recommend seeking professional legal and financial advice before entering any agreement.
                    </p>
                  </div>

                  <div className="border-t border-amber-200 pt-2 sm:pt-3">
                    <p className="font-semibold text-amber-900 mb-0.5 sm:mb-1">Data Privacy</p>
                    <p className="text-amber-800">
                      Your information is kept private and only shared with potential matches. See our privacy policy for details.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Terms Checkbox */}
              <Card>
                <CardContent className="pt-4 sm:pt-6 space-y-3 sm:space-y-4 p-4 sm:p-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      className="mt-0.5 sm:mt-1"
                    />
                    <label htmlFor="terms" className="text-xs sm:text-sm cursor-pointer">
                      I have read and agree to the{' '}
                      <a 
                        href="/jv-terms" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary underline hover:no-underline"
                      >
                        Terms and Conditions
                      </a>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Payment Button */}
          <div className="mt-6 sm:mt-8">
            <Button
              size="lg"
              className="w-full md:w-auto"
              onClick={handleProceedToPayment}
              disabled={loading || !phoneNumber || !!phoneError || !agreeToTerms}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Handshake className="mr-2 h-4 w-4" />
                  Pay KES {selectedPkgData?.price.toLocaleString()}
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}